import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useInView } from 'framer-motion';
import { Target, Eye, Cpu, Users, Globe2, Shield, ArrowRight, Sparkles } from 'lucide-react';
import SiteNavbar from '../components/SiteNavbar';
import AIAssistant from '../components/AIAssistant';

function Section({ children, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

function Badge({ children, variant = 'primary' }) {
  const variants = {
    primary: 'bg-primary/10 text-primary border border-primary/20',
    dark:    'bg-white/10 text-white/80 border border-white/20',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase ${variants[variant]}`}>
      {children}
    </span>
  );
}

export default function About() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const lang = i18n.language;

  const capabilities = [
    { icon: Cpu,    label: lang === 'ar' ? 'هندسة الذكاء الاصطناعي' : 'AI Engineering' },
    { icon: Globe2, label: lang === 'ar' ? 'تكاملات المنصات الصناعية' : 'Industrial Platform Integrations' },
    { icon: Users,  label: lang === 'ar' ? 'تصميم سير العمل' : 'Workflow Design' },
    { icon: Shield, label: lang === 'ar' ? 'الحوكمة والامتثال' : 'Governance & Compliance' },
  ];

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <SiteNavbar />
      <AIAssistant source="about" />

      {/* HERO */}
      <section className="relative bg-midnight -mt-16 pt-16 min-h-[65vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 blueprint-grid-dark opacity-40" />
        <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] rounded-full bg-primary/12 blur-[100px]" />
        <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] rounded-full bg-accent/8 blur-[80px]" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Badge variant="dark"><Sparkles size={12} />{t('about.badge')}</Badge>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight"
            >
              {t('about.headline')}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-5 text-white/60 text-base leading-relaxed max-w-2xl"
            >
              {t('about.subheadline')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* MISSION + VISION */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 border border-border-light"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                <Target size={22} className="text-primary" />
              </div>
              <h3 className="text-heading font-bold text-xl mb-3">{t('about.missionLabel')}</h3>
              <p className="text-body leading-relaxed">{t('about.mission')}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-midnight rounded-2xl p-8 border border-dark-border relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-primary/10 blur-[40px]" />
              <div className="relative z-10">
                <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center mb-5">
                  <Eye size={22} className="text-accent" />
                </div>
                <h3 className="text-white font-bold text-xl mb-3">{t('about.visionLabel')}</h3>
                <p className="text-white/65 leading-relaxed">{t('about.vision')}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* WHAT WE DO */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><Cpu size={12} />{lang === 'ar' ? 'ما نفعله' : 'What We Do'}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">
              {lang === 'ar' ? 'الحلول التي نبنيها' : 'The Solutions We Build'}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: lang === 'ar' ? 'منصة تحويل قوائم الكميات' : 'BoQ Conversion Platform',
                desc: lang === 'ar' ? 'تحويل أي صيغة لقائمة الكميات إلى مخرجات جاهزة لـERP خلال دقائق، مع مطابقة ذكية للكتالوج ومعالجة الاستثناءات.' : 'Convert any BoQ format into ERP-ready outputs in minutes, with AI catalog mapping and exception handling.',
                tag: lang === 'ar' ? 'الوحدة الأولى' : 'Module 1',
                color: 'bg-primary',
              },
              {
                title: lang === 'ar' ? 'منصة المستندات الفنية' : 'Technical Submittals Platform',
                desc: lang === 'ar' ? 'هيكلة المواصفات وبيانات التقنية، تحليل الامتثال بند بند، إنشاء حزم مستندات احترافية تلقائياً.' : 'Structure specs and datasheets, run compliance analysis, generate professional submittal packages automatically.',
                tag: lang === 'ar' ? 'الوحدة الثانية' : 'Module 2',
                color: 'bg-success',
              },
              {
                title: lang === 'ar' ? 'الحلول المخصصة' : 'Custom Industrial Solutions',
                desc: lang === 'ar' ? 'تصميم وبناء حلول مخصصة للتحديات الصناعية الفريدة. من الفكرة إلى التسليم الكامل.' : 'Design and build tailored solutions for unique industrial challenges. From concept to full delivery.',
                tag: lang === 'ar' ? 'مخصص' : 'Custom',
                color: 'bg-accent',
              },
              {
                title: lang === 'ar' ? 'حلول الشركات الصغيرة' : 'SME Digital Solutions',
                desc: lang === 'ar' ? 'تطبيق ERP مبني على Odoo، تكامل WhatsApp CRM، ورقمنة سير العمل للشركات النامية.' : 'Odoo ERP implementation, WhatsApp CRM integration, and workflow digitization for growing businesses.',
                tag: lang === 'ar' ? 'للشركات الصغيرة' : 'SME',
                color: 'bg-info',
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-border-light card-hover"
              >
                <div className={`inline-flex px-2 py-0.5 rounded-full ${item.color} text-white text-xs font-semibold mb-4`}>
                  {item.tag}
                </div>
                <h3 className="text-heading font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-body text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* CORE CAPABILITIES */}
      <Section className="py-20 bg-midnight relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid-dark opacity-40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="dark"><Shield size={12} />{lang === 'ar' ? 'قدراتنا الجوهرية' : 'Core Capabilities'}</Badge>
            <h2 className="mt-4 text-3xl font-black text-white">
              {lang === 'ar' ? 'ما نُتقنه' : 'What We Excel At'}
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {capabilities.map((cap, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-deep-surface rounded-xl p-5 border border-dark-border text-center card-hover"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center mx-auto mb-3">
                  <cap.icon size={18} className="text-primary" />
                </div>
                <p className="text-white font-medium text-sm">{cap.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto text-center px-4">
          <h2 className="text-3xl sm:text-4xl font-black text-heading">{lang === 'ar' ? 'تحدث مع فريقنا' : 'Talk to Our Team'}</h2>
          <p className="mt-3 text-body text-sm leading-relaxed">
            {lang === 'ar' ? 'أخبرنا عن تحديك — وسنوضح لك كيف يمكن لـ Tuly Technology حله.' : "Tell us your challenge — we'll show you exactly how Tuly Technology can solve it."}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary-hover transition-all shadow-primary-glow"
            >
              {t('about.ctaTeam')}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </button>
          </div>
        </div>
      </Section>

      <footer className="bg-midnight border-t border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/tuly.png" alt="Tuly Technology" className="h-7 w-auto" />
              <span className="text-white font-bold text-sm">Tuly Technology</span>
            </div>
            <p className="text-muted text-xs">© {new Date().getFullYear()} Tuly Technology. {t('footer.allRights')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
